const COUNTED_STATUSES = new Set(["pending", "approved"]);

const MONTH_SHORT = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

export function employeeNameFromRow(row) {
  return row?.employee?.username || String(row?.userId || "—");
}

export function requestTimestamp(row) {
  return row?.requestedAt || row?.requestedRecordedAt || null;
}

export function requestYear(row) {
  const iso = requestTimestamp(row);
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
}

function isCounted(row) {
  return COUNTED_STATUSES.has(row?.status);
}

function matchesYear(row, year) {
  if (year == null) return true;
  return requestYear(row) === year;
}

/**
 * Ranking de solicitudes de fichaje por empleado (pendientes + aprobadas).
 * `forgotten` = fichaje olvidado, `corrections` = corregir un fichaje existente.
 */
export function buildEmployeeRanking(rows = [], { year } = {}) {
  const byUser = new Map();

  rows.forEach((row) => {
    if (!isCounted(row) || !matchesYear(row, year)) return;
    const userId = row.userId ?? row.employee?.id ?? employeeNameFromRow(row);
    const current = byUser.get(userId) || {
      userId,
      name: employeeNameFromRow(row),
      forgotten: 0,
      corrections: 0,
      total: 0,
    };
    if (row.requestType === "add_missing") current.forgotten += 1;
    else current.corrections += 1;
    current.total = current.forgotten + current.corrections;
    if (row.employee?.username) current.name = row.employee.username;
    byUser.set(userId, current);
  });

  return Array.from(byUser.values())
    .map((row) => ({
      ...row,
      axisLabel: `${row.name} (${row.total})`,
    }))
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.name.localeCompare(b.name, "es");
    });
}

export function buildMonthlySeries(rows = [], year) {
  const months = MONTH_SHORT.map((label, idx) => ({
    month: idx + 1,
    label,
    forgotten: 0,
    corrections: 0,
    total: 0,
  }));

  rows.forEach((row) => {
    if (!isCounted(row) || !matchesYear(row, year)) return;
    const iso = requestTimestamp(row);
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return;
    const bucket = months[d.getMonth()];
    if (!bucket) return;
    if (row.requestType === "add_missing") bucket.forgotten += 1;
    else bucket.corrections += 1;
    bucket.total = bucket.forgotten + bucket.corrections;
  });

  return months;
}

export function rankingHeadline(ranking = []) {
  const total = ranking.reduce((sum, row) => sum + row.total, 0);
  const forgotten = ranking.reduce((sum, row) => sum + row.forgotten, 0);
  const top = ranking[0] || null;
  return { total, forgotten, top };
}
